import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Filter, Search, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { runHistory } from "../api/history";
import { RunCard } from "../components/RunCard";
import type { RunSummary } from "../types/history";

export function RunHistoryPage() {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRuns, setTotalRuns] = useState(0);

  const PAGE_SIZE = 10;

  useEffect(() => {
    loadRunHistory();
  }, [currentPage]);

  const loadRunHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await runHistory.getUserRunHistory(currentPage, PAGE_SIZE);
      setRuns(result.runs);
      setTotalRuns(result.total);
    } catch (err) {
      console.error('Failed to load run history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const filteredRuns = runs.filter(run => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (run.club?.toLowerCase().includes(searchLower)) ||
      (run.swing_type?.toLowerCase().includes(searchLower)) ||
      run.primary_flaws.some(flaw => 
        flaw.title.toLowerCase().includes(searchLower) ||
        flaw.flaw_id.toLowerCase().includes(searchLower)
      )
    );
  });

  const totalPages = Math.ceil(totalRuns / PAGE_SIZE);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPrevPage = currentPage > 0;

  if (loading && runs.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/swing-lab">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Swing Lab
              </Button>
            </Link>
          </div>
          
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-8 bg-gray-200 rounded w-full"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/swing-lab">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Swing Lab
            </Button>
          </Link>
          
          <div className="flex items-center gap-2">
            <History className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold text-gray-900">Analysis History</h1>
          </div>
        </div>

        {/* Search and Stats */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by club, swing type, or flaw..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {totalRuns} total analysis{totalRuns === 1 ? '' : 'es'}
            </Badge>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <Alert className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Empty State */}
        {!loading && runs.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No analysis history yet
              </h3>
              <p className="text-gray-600 mb-6">
                Upload your first swing video to start tracking your progress.
              </p>
              <Link to="/swing-lab/upload">
                <Button>Upload Your First Swing</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Runs List */}
        {filteredRuns.length > 0 && (
          <>
            <div className="space-y-4 mb-8">
              {filteredRuns.map((run) => (
                <RunCard key={run.analysis_id} run={run} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={!hasPrevPage}
                >
                  Previous
                </Button>
                
                <span className="text-sm text-gray-600">
                  Page {currentPage + 1} of {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!hasNextPage}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}

        {/* No Search Results */}
        {searchTerm && filteredRuns.length === 0 && runs.length > 0 && (
          <Card className="text-center py-8">
            <CardContent>
              <p className="text-gray-600">
                No analyses found matching "{searchTerm}"
              </p>
              <Button
                variant="ghost"
                onClick={() => setSearchTerm("")}
                className="mt-2"
              >
                Clear search
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}